/* eslint-disable @typescript-eslint/no-empty-function */
import { createContext, useState, useContext, FC, ReactNode } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import Button, { ButtonProps } from '@mui/material/Button'
import TextField from '@mui/material/TextField'
interface ActionButtonProps extends ButtonProps {
  text?: string
}
interface Actions {
  cancel?: ActionButtonProps
  ok?: ActionButtonProps
}
interface ContextProps {
  showConfirmation: (
    callback: () => void,
    comp: ReactNode,
    confirmText?: string,
    buttonProps?: Actions,
    cancelCallback?: () => void,
  ) => void
  hideConfirmation: () => void
  isShown: boolean
  onOkAction?: (() => void) | undefined
  onCancelAction?: (() => void) | undefined
  comp?: ReactNode
  confirmText?: string
  buttonProps?: Actions
}
export const ConfirmDialogContext = createContext<ContextProps>({
  showConfirmation: () => {},
  hideConfirmation: () => {},
  isShown: false,
  onOkAction: () => {},
  onCancelAction: () => {},
  comp: undefined,
  confirmText: undefined,
  buttonProps: undefined,
})

/**
 * Common confirm dialog to be shown
 */
const ConfirmDialog = () => {
  const { hideConfirmation, isShown, onOkAction, onCancelAction, comp, confirmText, buttonProps } =
    useContext(ConfirmDialogContext)
  const { text: textOk = 'OK', ...restOk } = (buttonProps || { ok: { text: 'OK' } }).ok || {}
  const { text: textCancel = 'Cancel', ...restCancel } = (buttonProps || { cancel: { text: 'Cancel' } }).cancel || {}
  const [confirmTextValue, setConfirmTextValue] = useState('')
  const [closeReason, setCloseReason] = useState<'ok' | 'cancel'>('cancel')

  const handleOk = () => {
    setCloseReason('ok')
    hideConfirmation()
    setConfirmTextValue('')
  }

  const handleClose = () => {
    setCloseReason('cancel')
    hideConfirmation()
    setConfirmTextValue('')
  }

  /* Callback after transition */
  const handleTransitionExited = () => {
    closeReason === 'ok' ? onOkAction?.() : onCancelAction?.()
  }

  return (
    <Dialog
      open={isShown}
      disableRestoreFocus
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return
        handleClose()
      }}
      onTransitionExited={handleTransitionExited}
      data-test-id="confirm-dialog"
    >
      <DialogContent>
        <DialogContentText gutterBottom component="div">
          {comp}
        </DialogContentText>
        {confirmText && (
          <>
            <DialogContentText gutterBottom>
              To confirm, type{' '}
              <span style={{ fontStyle: 'italic', fontWeight: 'bold' }} data-test-id="confirm-text-value">
                {confirmText?.toLowerCase()}
              </span>{' '}
              below
            </DialogContentText>
            <TextField
              data-test-id="confirm-text-field"
              autoFocus
              fullWidth
              placeholder={confirmText?.toLowerCase()}
              value={confirmTextValue}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && confirmTextValue.toLowerCase() === confirmText.toLowerCase()) {
                  handleOk()
                }
              }}
              onChange={(e) => setConfirmTextValue(e.target.value)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={handleClose} data-test-id="cancel-btn" {...restCancel}>
          {textCancel}
        </Button>
        <Button
          data-test-id="ok-btn"
          variant="contained"
          color="primary"
          disabled={confirmText ? confirmTextValue.toLowerCase() !== confirmText?.toLowerCase() : false}
          onClick={handleOk}
          {...restOk}
        >
          {textOk}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

type Props = {
  children: ReactNode
}

/**
 * Provider to use to show simple confirmation dialog
 * To invoke call useConfirmationDialog from common utils
 */
export const ConfirmDialogProvider: FC<Props> = ({ children }) => {
  const [state, setState] = useState<
    Pick<ContextProps, 'isShown' | 'onOkAction' | 'comp' | 'confirmText' | 'buttonProps' | 'onCancelAction'>
  >({
    isShown: false,
    onOkAction: undefined,
    comp: undefined,
    buttonProps: undefined,
  })
  const value = {
    showConfirmation: (
      onOkAction: () => void,
      comp: React.ReactNode,
      confirmText?: string,
      buttonProps?: Actions,
      onCancelAction?: () => void,
    ) =>
      setState({
        isShown: true,
        onOkAction,
        onCancelAction,
        comp,
        confirmText,
        buttonProps,
      }),
    hideConfirmation: () =>
      setState({
        isShown: false,
        onOkAction: state.onOkAction,
        comp: state.comp,
        confirmText: state.confirmText,
        buttonProps: state.buttonProps,
      }),
    ...state,
  }
  return (
    <ConfirmDialogContext.Provider value={value}>
      {children}
      <ConfirmDialog />
    </ConfirmDialogContext.Provider>
  )
}

export enum ConfirmationDialogResult {
  OK,
  Cancel,
}

export const useConfirmationDialog = () => {
  const { showConfirmation, hideConfirmation } = useContext(ConfirmDialogContext)

  const showConfirmationAsync = (comp: React.ReactNode, confirmText?: string, buttonProps?: Actions) => {
    return new Promise<ConfirmationDialogResult>((resolve) => {
      showConfirmation(
        () => resolve(ConfirmationDialogResult.OK),
        comp,
        confirmText,
        buttonProps,
        () => resolve(ConfirmationDialogResult.Cancel),
      )
    })
  }

  return { showConfirmation, showConfirmationAsync, hideConfirmation }
}
